<template>
  <v-dialog v-model="dialog" max-width="650" persistent>
    <template v-slot:activator="{ on, attrs }">
      <v-btn color="primary" v-bind="attrs" v-on="on">
        <v-icon left>mdi-plus</v-icon> Add Policy
      </v-btn>
    </template>
    <v-card>
      <ModalHeader
        title="Add Warranty Policy"
        icon="mdi-shield-check"
        @close="dialog = false"
      />
      <v-card-text class="pa-4">
        <v-text-field
          outlined
          dense
          v-model="form.name"
          label="Policy Name"
          prepend-icon="mdi-shield-check"
          :rules="[rules.required]"
        ></v-text-field>
        <v-textarea
          outlined
          dense
          v-model="form.description"
          label="Description"
          prepend-icon="mdi-text"
          rows="2"
          auto-grow
        ></v-textarea>

        <v-select
          outlined
          dense
          v-model="form.warranty_type"
          :items="warrantyTypeOptions"
          label="Warranty Type"
          prepend-icon="mdi-tag"
          :rules="[rules.required]"
          hint="Determines which warranty terms are printed on the certificate"
          persistent-hint
        ></v-select>

        <v-divider class="my-3"></v-divider>
        <p class="subtitle-2 grey--text">Warranty Durations</p>

        <v-row dense>
          <v-col cols="6">
            <v-text-field
              outlined
              dense
              v-model.number="form.major_parts_warranty_value"
              label="Major Parts Warranty"
              type="number"
              min="0"
              prepend-icon="mdi-cog"
            ></v-text-field>
          </v-col>
          <v-col cols="6">
            <v-select
              outlined
              dense
              v-model="form.major_parts_warranty_unit"
              :items="unitOptions"
              label="Unit"
            ></v-select>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="6">
            <v-text-field
              outlined
              dense
              v-model.number="form.minor_parts_warranty_value"
              label="Minor Parts Warranty"
              type="number"
              min="0"
              prepend-icon="mdi-cog-outline"
            ></v-text-field>
          </v-col>
          <v-col cols="6">
            <v-select
              outlined
              dense
              v-model="form.minor_parts_warranty_unit"
              :items="unitOptions"
              label="Unit"
            ></v-select>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="6">
            <v-text-field
              outlined
              dense
              v-model.number="form.service_warranty_value"
              label="Service Warranty"
              type="number"
              min="0"
              prepend-icon="mdi-wrench"
            ></v-text-field>
          </v-col>
          <v-col cols="6">
            <v-select
              outlined
              dense
              v-model="form.service_warranty_unit"
              :items="unitOptions"
              label="Unit"
            ></v-select>
          </v-col>
        </v-row>

        <v-divider class="my-3"></v-divider>
        <p class="subtitle-2 grey--text">Chip Color</p>
        <div class="d-flex flex-wrap" style="gap: 8px">
          <v-chip
            v-for="c in colorOptions"
            :key="c.value"
            :color="c.value"
            dark
            small
            :outlined="form.color !== c.value"
            @click="form.color = c.value"
            class="cursor-pointer"
            >{{ c.text }}</v-chip
          >
        </div>

        <v-switch
          v-model="form.is_active"
          label="Active"
          color="green"
        ></v-switch>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="close">Cancel</v-btn>
        <v-btn color="primary" :loading="loading" @click="submit">Save</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from "vuex";
export default {
  data() {
    return {
      dialog: false,
      loading: false,
      form: {
        name: "",
        description: "",
        major_parts_warranty_value: 12,
        major_parts_warranty_unit: "months",
        minor_parts_warranty_value: 6,
        minor_parts_warranty_unit: "months",
        service_warranty_value: 1,
        service_warranty_unit: "months",
        is_active: true,
        color: "blue",
        warranty_type: "regular",
      },
      warrantyTypeOptions: [
        { text: "Regular (Per-Item Warranty)", value: "regular" },
        { text: "Desktop PC Sets", value: "desktop" },
        { text: "Laptop", value: "laptop" },
        { text: "CCTV Systems", value: "cctv" },
        { text: "Repairs & Replacements", value: "repairs" },
        { text: "Printer", value: "printer" },
      ],
      colorOptions: [
        { text: "Blue", value: "blue" },
        { text: "Teal", value: "teal" },
        { text: "Purple", value: "purple" },
        { text: "Indigo", value: "indigo" },
        { text: "Cyan", value: "cyan" },
        { text: "Green", value: "green" },
        { text: "Orange", value: "orange" },
        { text: "Red", value: "red" },
        { text: "Pink", value: "pink" },
        { text: "Brown", value: "brown" },
        { text: "Blue Grey", value: "blue-grey" },
        { text: "Deep Purple", value: "deep-purple" },
      ],
      unitOptions: [
        { text: "Days", value: "days" },
        { text: "Months", value: "months" },
        { text: "Years", value: "years" },
      ],
      rules: {
        required: (v) => !!v || "Required",
      },
    };
  },
  methods: {
    ...mapActions({
      add_warranty_policy: "warranty_policy/add_warranty_policy",
    }),
    async submit() {
      if (!this.form.name) return;
      this.loading = true;
      try {
        await this.add_warranty_policy(this.form);
        this.close();
      } catch (e) {
        console.error(e);
      }
      this.loading = false;
    },
    close() {
      this.dialog = false;
      this.form = {
        name: "",
        description: "",
        major_parts_warranty_value: 12,
        major_parts_warranty_unit: "months",
        minor_parts_warranty_value: 6,
        minor_parts_warranty_unit: "months",
        service_warranty_value: 1,
        service_warranty_unit: "months",
        is_active: true,
        color: "blue",
        warranty_type: "regular",
      };
    },
  },
};
</script>
